import Config from "../config";
import lodash, { toNumber } from "lodash-es";
import { getNoWeekTimeCourseList, JsonRequest } from "./index";
import { isTrue } from "../Utils/filter";

const customerCode = Config.CustomCode;

export enum TopicType {
    Discuss = "0", // 讨论
    Ask = "1" // 答疑
}

export interface CommonDiscussParams {
    batchNo: string;
    courseNo: string;
    courseSeq: string;
}

export interface DiscussListItem {
    uuid: string;
    topicFiles: null;
    replies: null;
    statics?: {
        partInCount: number; // 参与数
        replyCount: number; // 发言数
    };
    topicName: string;
    topicContent: string;
    customerCode: string;
    batchNo: string;
    courseNo: string;
    courseSeq: string;
    courseName: string;
    weektimeId: number;
    topicDurationDay: number;
    topicDurationHour: number;
    topicDurationMinute: number;
    deadline: string;
    showLike: BooleanValue; // 是否展示点赞 0或空否1是
    showDislike: BooleanValue; // 是否展示点踩 0或空否1是
    wordCountLimit: number; //最小发言字数
    showStudentName: BooleanValue; // 是否展示学生姓名 0或空否1是
    canAnonymousSpeak: BooleanValue; // 是否匿名发言 0或空否1是
    canMutualReplay: BooleanValue; // 是否相互评论 0或空否1是
    isStart: BooleanValue; // 是否开始（0或空不开始，1开始（保存之后立即开始））
    delStatus: number;
    createTime: string;
    updateTime: string;
    statusQuery: {
        no: string;
        status: BooleanValue;
    };
}
/**
 * 查询主题讨论列表
 * @param args
 */
export function findDiscussList(
    args: {
        weektimeId: number;
        sort?: string;
    } & CommonDiscussParams
) {
    return JsonRequest.post<SuccessData<DiscussListItem[]>>(
        "/student/discuss/findDiscussList",
        {
            ...args,
            customerCode
        }
    ).then(res => res.data.data);
}

/**
 * 古怪的日期格式转成秒
 * @param data
 */
export function getDurationSeconds(data: {
    topicDurationDay?: number;
    topicDurationHour?: number;
    topicDurationMinute?: number;
}) {
    const { topicDurationDay, topicDurationHour, topicDurationMinute } = data;

    return (
        toNumber(topicDurationDay) * 3600 * 24 +
        toNumber(topicDurationHour) * 3600 +
        toNumber(topicDurationMinute) * 60
    );
}

export enum SortType {
    All = 0, // 全部
    Hot = 1, // 热门
    New = 2 // 最新
}

interface DiscussStatics {
    partInCount?: number;
    replyCount?: number;
}
export interface DiscussTopicDetail {
    courseName?: string;
    uuid: string;
    topicName: string;
    topicContent: string;
    customerCode: string;
    topicDurationDay: number;
    topicDurationHour: number;
    topicDurationMinute: number;
    deadline: string | null;
    showLike?: "0" | "1"; // 是否展示点赞 0或空否1是
    showDislike?: "0" | "1"; // 是否展示点踩 0或空否1是
    wordCountLimit: number;
    showStudentName?: "0" | "1"; // 是否展示学生姓名 0或空否1是
    canAnonymousSpeak?: "0" | "1"; // 是否匿名发言 0或空否1是
    canMutualReplay?: "0" | "1"; // 是否相互评论 0或空否1是
    canEdit?: BooleanValue; // 是否可以编辑 0或空否1是
    isStart?: "0" | "1"; // 是否开始（0或空不开始，1开始（保存之后立即开始））
    createTime: string;
    updateTime: string;
    statics?: DiscussStatics;
    // todo 主题接口没有返回发帖人姓名和学号, 还有类型
}

// 发言人类型（0教师1学生）
export enum SpeakerType {
    Teacher = "0",
    Student = "1"
}

interface LikeListItem {
    no: string; // 点赞人编号
    name: string; // 点赞人姓名
    type: SpeakerType;
}

export interface ReplyItemData {
    uuid: string;
    topicUuid: string;
    currentFloorReplies?: ReplyItemData[];
    likes?: LikeListItem[];
    dislikes?: LikeListItem[];
    replyContent: string;
    isFloor: string;
    ownerFloorUuid: null;
    anonymousSpeak: BooleanValue;
    speakerNo: string;
    speakerName: string;
    speakerType: SpeakerType;
    speakToReplyUuid?: string;

    speakToNo: null;
    speakToName: null;
    speakToType: null;
    likeCount?: number;
    dislikeCount?: number;
    score: number;
    canShow: BooleanValue; // 是否可见（0否1是）
    isTop: BooleanValue;
    delStatus: BooleanValue;
    createTime: string;
    updateTime: string;
    topicType: TopicType;
    canEdit: BooleanValue; //是否可编辑
}

export interface DiscussDetail extends DiscussTopicDetail {
    speakerNo: string;
    speakerName: string;
    speakerType: SpeakerType;
    replies?: ReplyItemData[];
}

/**
 * 按树形层级整理回复帖
 * @param data
 */
function reArrangeReplies(data: ReplyItemData[]): ReplyItemData[] {
    if (!data) return [];

    const temp: Record<string, ReplyItemData> = {};

    function findRootReply(
        speakToReplyUuid: string
    ): ReplyItemData | undefined {
        const reply = temp[speakToReplyUuid];
        if (!reply) return undefined; // 没找到父级评论, 则该子评论也不显示
        if (!reply.speakToReplyUuid) return reply;
        return findRootReply(reply.speakToReplyUuid);
    }

    for (const reply of data) {
        const { uuid, speakToReplyUuid } = reply;
        temp[uuid] = reply;

        if (!speakToReplyUuid) continue;
        const speakToReply = findRootReply(speakToReplyUuid);
        if (!speakToReply) continue;
        if (!speakToReply.currentFloorReplies)
            speakToReply.currentFloorReplies = [];
        speakToReply.currentFloorReplies.push(reply);
    }

    return data.filter(item => !item.speakToReplyUuid);
}

export function getDiscussDetail(
    args: CommonDiscussParams & { uuid: string; sort?: SortType }
) {
    return JsonRequest.post<SuccessData<DiscussDetail>>(
        "/student/discuss/partInDiscuss",
        {
            ...args,
            customerCode
        }
    )
        .then(res => res.data.data)
        .then(data => {
            const { replies } = data;

            replies?.forEach(reply => {
                reply.currentFloorReplies = reArrangeReplies(
                    reply.currentFloorReplies || []
                );
            });

            return { ...data };
        });
}

export interface DiscussTopicForm extends CommonDiscussParams {
    uuid: string; //
    topicName: string; // 主题讨论名称（为空时，后台自动生成一个名称，如果是修改的话最好需要带上这个字段）
    topicContent: string; // 主题讨论内容
    weektimeId: number;
    topicDuration: number; // 持续天数（为空时表示0）
    showLike?: BooleanValue; // 是否展示点赞 0或空否1是
    showDislike?: BooleanValue; // 是否展示点踩 0或空否1是
    wordCountLimit: number; // 最小字数
    showStudentName?: BooleanValue; // 是否展示学生姓名 0或空否1是
    canAnonymousSpeak?: BooleanValue; // 是否匿名发言 0或空否1是
    canMutualReplay?: BooleanValue; // 是否相互评论 0或空否1是
    isStart?: BooleanValue; // 是否开始（0或空不开始，1开始（保存之后立即开始））
}

export interface QueryAskListArgs {
    keyword?: string;
    query?: string;
    sort: BooleanValue;
}

export interface AskStatics {
    partInCount: number;
    replyCount: number;
}

export interface AskListItem {
    id: null;
    replies: null;
    files: null;
    statusQuery: null;
    statics: AskStatics;
    uuid: string;
    questionTitle: string;
    questionContent: string;
    customerCode: string;
    batchNo: string;
    courseNo: string;
    courseSeq: string;
    courseName: string;
    allowStudentAnswer: BooleanValue;
    anonymousAsk: BooleanValue;
    realAskerNo: string;
    askerNo: string;
    askerName: string;
    shieldTeacherNo: null;
    partInTeacherNo: null;
    delStatus: number;
    createTime: string;
    updateTime: string;
    query: null;
    keyword: null;
    sort: null;
    canEdit: BooleanValue;
    hasPartIn: BooleanValue; // 是否已参与（0否1是）用来判断是否显示红点
}

/**
 * 查询答疑列表
 * @param query
 * @param batchNo
 */
export function queryAskList(query: QueryAskListArgs, batchNo: string) {
    query = lodash.cloneDeep(query);

    return JsonRequest.post<SuccessData<AskListItem[]>>(
        "/student/answering/queryList",
        {
            ...query,
            customerCode,
            batchNo
        }
    ).then(res => res.data.data);
}

export interface AskTeacherDetail {
    id: null;
    replies?: ReplyItemData[];
    files: null;
    statusQuery: null;
    statics?: DiscussStatics;
    uuid: string;
    questionTitle: string;
    questionContent: string;
    customerCode: string;
    batchNo: string;
    courseNo: string;
    courseSeq: string;
    courseName: string;
    allowStudentAnswer: BooleanValue;
    anonymousAsk: BooleanValue;
    realAskerNo: string;
    askerNo: string;
    askerName: string;
    shieldTeacherNo: null;
    partInTeacherNo: string;
    delStatus: number;
    createTime: string;
    updateTime: string;
    hasPartIn: null;
}

export function isAskDetail(
    data: AskTeacherDetail | DiscussDetail | undefined | null
): data is AskTeacherDetail {
    if (data === null || data === undefined) return false;
    return "askerNo" in data;
}

/**
 * 把 AskTeacherDetail类型的数据尽量变成 DiscussDetail类型
 * 如果某些字段 AskTeacherDetail类型中不存在, 填充一个 --尽量合理-- 的默认值
 * 同时保留AskTeacherDetail特有数据
 * 这样需要AskTeacherDetail特有数据时, 可用 isAskDetail(data) && data.hasPartIn类似这种写法来读取
 * @param data
 */
export function convertDiscussDetail(data: AskTeacherDetail): DiscussDetail {
    return {
        ...data,
        // 这些是相同字段不同命名
        topicName: data.questionTitle,
        topicContent: data.questionContent,
        speakerNo: data.askerNo,
        speakerName: data.askerName,
        speakerType: SpeakerType.Student,
        // 这些是讨论需要而答疑不需要的配置项
        canAnonymousSpeak: "1",
        canMutualReplay: "1",
        showStudentName: "1",
        isStart: "1",
        topicDurationDay: 0,
        topicDurationHour: 0,
        topicDurationMinute: 0,
        showDislike: "1",
        showLike: "1",
        deadline: null,
        wordCountLimit: 0
    };
}

/**
 * 答疑详细
 * @param args
 */
export function getAskTeacherDetail(
    args: CommonDiscussParams & { uuid: string; sort: SortType }
) {
    return JsonRequest.post<SuccessData<AskTeacherDetail>>(
        "/student/answering/partInAnsweringQuestion",
        {
            ...args,
            customerCode
        }
    )
        .then(res => res.data.data)
        .then(data => {
            const { replies } = data;

            replies?.forEach(reply => {
                reply.currentFloorReplies = reArrangeReplies(
                    reply.currentFloorReplies || []
                );
            });

            return { ...data };
        });
}
export interface ReplyFormData {
    replyContent: string;
    anonymousSpeak: BooleanValue;
}

interface ReplyArgs extends ReplyFormData {
    topicType: TopicType;
    speakToReplyUuid?: string; // 回复的评论uuid
}

/**
 * 帖子回复
 * @param topicUuid
 * @param ownerFloorUuid
 * @param args
 */
export function postReply(
    topicUuid: string,
    ownerFloorUuid: string, // 所属楼层uuid
    args: ReplyArgs
) {
    args = lodash.cloneDeep(args);

    return JsonRequest.post("/student/reply/reply", {
        ...args,
        anonymousSpeak: isTrue(args.anonymousSpeak) ? "1" : "0",
        topicUuid,
        ownerFloorUuid,
        isFloor: "0"
    });
}

/**
 * 点踩或取消点踩
 * @param topicUuid
 * @param replyUuid
 */
export function dislikeOrCancel(topicUuid: string, replyUuid: string) {
    return JsonRequest.get(
        `/student/reply/dislikeOrCancel/${topicUuid}/${replyUuid}`
    );
}

/**
 * 点赞或取消点赞
 * @param topicUuid
 * @param replyUuid
 */
export function likeOrCancel(topicUuid: string, replyUuid: string) {
    return JsonRequest.get(
        `/student/reply/likeOrCancel/${topicUuid}/${replyUuid}`
    );
}

export interface AnswerFormData {
    topicType: TopicType; // 主题类型（0主题讨论 1答疑）
    replyContent: string; // 回复内容
    anonymousSpeak: BooleanValue; // 是否匿名评论
}

/**
 * 发表回答
 * @param topicUuid
 * @param args
 */
export function postAnswer(topicUuid: string, args: AnswerFormData) {
    args = lodash.cloneDeep(args);
    return JsonRequest.post("/student/reply/reply", {
        ...args,
        topicUuid,
        isFloor: "1",
        anonymousSpeak: isTrue(args.anonymousSpeak) ? "1" : "0"
    });
}

/**
 * 编辑回复
 * @param topicUuid
 * @param uuid
 * @param data
 */
export function editPost(
    topicUuid: string,
    uuid: string,
    data: {
        replyContent: string;
        anonymousSpeak: BooleanValue; // 是否匿名评论
    }
) {
    const { replyContent, anonymousSpeak } = lodash.cloneDeep(data);
    return JsonRequest.post("/student/reply/editReply", {
        replyContent,
        anonymousSpeak: isTrue(anonymousSpeak) ? "1" : "0",
        topicUuid,
        uuid
    });
}

/**
 * 计算回复数量
 * @param data
 */
export function getReplyCount(data: ReplyItemData[]): number {
    return data.reduce((memo, reply) => {
        return memo + 1 + getReplyCount(reply.currentFloorReplies || []);
    }, 0);
}

/**
 * 删除回复
 * @param topicUuid
 * @param uuid
 */
export function deleteReply(topicUuid: string, uuid: string) {
    return JsonRequest.post("/student/reply/deleteReply", { topicUuid, uuid });
}

/**
 * 提问 add/edit
 */
export interface QuestionFormParams {
    questionTitle: string; // 必须，问题标题
    questionContent: string; // 必须，问题内容
    allowStudentAnswer?: BooleanValue; // 是否允许学生回答 0 否 1 是，非必须
    anonymousAsk?: BooleanValue; // 是否匿名提问 0 否 1 是，非必须
    courseKey?: string;
}
export interface QuestionParams {
    uuid?: string; //答疑uuid（如果为空则新增，不为空为更新）
    customerCode: string; // 必须，租户代码
    batchNo: string; // 必须，学期编号
    courseNo?: string; // 必须，课程编号
    courseSeq?: string; // 必须，课程序号
    courseName?: string; // 必须，课程名称
}
export type PostQuestionParams = QuestionParams & QuestionFormParams;
export function answerQuestion(params: PostQuestionParams) {
    const data = lodash.cloneDeep(params);
    return JsonRequest.post("/student/answering/answerQuestion", data);
}

//答疑明细
export function getAnsweringQuestionDetail(data: QuestionParams) {
    return JsonRequest.post<SuccessData<PostQuestionParams>>(
        "/student/answering/getAnsweringQuestionDetail",
        data
    ).then(res => res.data.data);
}

//删除答疑
type DeleteQuestionParams = Omit<QuestionParams, "courseName">;
export function deleteAnsweringQuestion(data: DeleteQuestionParams) {
    return JsonRequest.post(
        "/student/answering/deleteAnsweringQuestion",
        data
    ).then(res => res.data.data);
}

//保存通知设置
//主题讨论
export interface ProjectSetParams {
    hasNewFloor: string; // 有新发言
    hasNewReply: string; // 我的发言有新回复
    hasNewLik: string; // 我的发言有新点赞
    hasNewDislike: string; // 我的发言有新点踩
}
//答疑
export interface AskSetParams {
    myQuestionHasNewFloor: string; // 我提问的通知有新楼层
    myQuestionHasNewReply: string; // 我的提问有新回复
    myFloorHasUpgrade: string; // 我的评论的答疑有新更新
    myReplyHasUpgrade: string; // 我的回复的答疑有更新通知
}
export enum BusinessKey {
    Discuss = "discuss",
    Aks = "answerQuestion"
}
export interface NoticeSetProps {
    noticeConfigJson: AskSetParams | ProjectSetParams;
    businessKey: BusinessKey;
}

export function isAskSet(
    data: AskSetParams | ProjectSetParams | undefined | null
): data is AskSetParams {
    if (data === null || data === undefined) return false;
    return "myQuestionHasNewFloor" in data;
}

export function isDiscSet(
    data: AskSetParams | ProjectSetParams | undefined | null
): data is ProjectSetParams {
    if (data === null || data === undefined) return false;
    return "hasNewReply" in data;
}

export function saveNoticeSet(data: NoticeSetProps) {
    return JsonRequest.post("/student/notice/saveNoticeSet", {
        ...data,
        noticeConfigJson: JSON.stringify(data.noticeConfigJson)
    }).then(res => res.data.data);
}
type GetNoticeProps = Omit<NoticeSetProps, "noticeConfigJson">;
export function getNoticeSet(params: GetNoticeProps) {
    return JsonRequest.get<SuccessData<NoticeSetProps>>(
        `/student/notice/getNoticeSet/${params.businessKey}`
    ).then(res => {
        const o = res.data.data;
        if (o.noticeConfigJson) {
            // @ts-ignore
            o.noticeConfigJson = JSON.parse(o.noticeConfigJson);
        }
        return o;
    });
}

export async function getCourseOptions(batchNo: string) {
    const res = await getNoWeekTimeCourseList(batchNo);
    return res.map(item => {
        return {
            ...item,
            key: `${item.courseName}-${item.courseNo}-${item.courseSeq}`
        };
    });
}
